import { CreateNotifications, NotificationType } from '@/types/notification.type';
import { useEffect, useMemo, useState } from 'react';

interface MessageContentProps {
  messageData: CreateNotifications;
  setMessageData: React.Dispatch<React.SetStateAction<CreateNotifications>>;
}

export default function MessageContent({ messageData, setMessageData }: MessageContentProps) {
  // const location = useLocation();
  // const editData = (location.state as any)?.notification ?? null;

  // useEffect(() => {
  //   if (editData) {
  //     setMessageData((prev) => ({
  //       ...prev,
  //       title: editData.title ?? prev.title,
  //       message: editData.message ?? prev.message,

  //       audience:
  //         editData.type === 'Push'
  //           ? 'all'
  //           : editData.type === 'In App'
  //             ? 'filter'
  //             : editData.type === 'Both'
  //               ? 'upload'
  //               : prev.targetGroup,

  //       dateTime: editData.scheduledSend ?? prev.scheduledTime,
  //     }));
  //   }
  // }, [editData, setMessageData]);

  const [errors, setErrors] = useState({
    title: '',
    message: '',
  });

  const [templateValues, setTemplateValues] = useState<Record<string, string>>({});
  const [showTooltip, setShowTooltip] = useState(false);

  const extractTemplateVariables = (text: string): string[] => {
    const regex = /\{\{\s*(\w+)\s*\}\}/g;
    const variables: string[] = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (!variables.includes(match[1])) {
        variables.push(match[1]);
      }
    }
    return variables;
  };

  const templateVariables = useMemo(
    () => extractTemplateVariables(messageData.message),
    [messageData.message],
  );

  useEffect(() => {
    setTemplateValues((prev) => {
      const newValues: Record<string, string> = {};
      templateVariables.forEach((variable) => {
        newValues[variable] = prev[variable] || messageData.templateValues?.[variable] || '';
      });
      return newValues;
    });
  }, [templateVariables, messageData.templateValues]);

  // const handleTemplateValueChange = (variable: string, value: string) => {
  //   const newTemplateValues = { ...templateValues, [variable]: value };
  //   setTemplateValues(newTemplateValues);
  //   setMessageData((prev) => ({ ...prev, templateValues: newTemplateValues }));
  // };

  // const previewMessage = useMemo(() => {
  //   let preview = messageData.message;
  //   Object.entries(templateValues).forEach(([key, value]) => {
  //     preview = preview.replace(
  //       new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'),
  //       value || `{{${key}}`,
  //     );
  //   });
  //   return preview;
  // }, [messageData.message, templateValues]);

  const validateTitle = (value: string) => {
    if (!value.trim()) return 'Title is required';
    if (value.length < 5) return 'Title must be at least 5 characters';
    if (value.length > 30) return 'Title cannot exceed 30 characters';
    return '';
  };

  const validateMessage = (value: string) => {
    if (!value.trim()) return 'Message is required';
    if (value.length < 10) return 'Message must be at least 10 characters';
    if (value.length > 500) return 'Message cannot exceed 500 characters';
    return '';
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMessageData((prev) => ({ ...prev, title: value }));
    setErrors((prev) => ({ ...prev, title: validateTitle(value) }));
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setMessageData((prev) => ({ ...prev, message: value }));
    setErrors((prev) => ({ ...prev, message: validateMessage(value) }));
  };

  const options: { key: NotificationType; label: string; desc: string }[] = [
    {
      key: 'PUSH_NOTIFICATION',
      label: 'Push Notification',
      desc: 'Send to user when app is closed',
    },
    {
      key: 'IN_APP_NOTIFICATION',
      label: 'In-App Notification',
      desc: 'Send when app is open',
    },
    {
      key: 'BOTH',
      label: 'Both',
      desc: 'Maximum reach with both delivery methods',
    },
  ];

  return (
    <div className="w-full bg-[#FAF8F9] rounded-xl">
      <div className="px-6 py-5 border-b border-[#87074C]">
        <h2 className="text-2xl font-bold text-[#621C43] mb-1">Message Content</h2>
        <p className="text-lg font-medium text-[#621C43]">
          Enter the basic details for the new notification
        </p>
      </div>

      <div>
        <p className="px-6 pt-1 text-[#4A082D] mb-1">
          Notification Type <span className="text-red-500">*</span>
        </p>

        <div className="flex gap-4 px-6 pb-2">
          {options.map((option) => {
            const isSelected = messageData.notificationType === option.key;
            return (
              <label
                key={option.key}
                className={`flex-1 cursor-pointer border rounded-xl p-4 transition-all
                  ${
                    isSelected
                      ? 'border-[#621C43] bg-[#FFF8ED]/30'
                      : 'border-gray-300 bg-[#FFF8ED]/30'
                  }
                `}
              >
                <input
                  type="radio"
                  className="hidden"
                  name="notificationType"
                  value={option.key}
                  checked={isSelected}
                  onChange={() =>
                    setMessageData((prev) => ({
                      ...prev,
                      notificationType: option.key,
                    }))
                  }
                />

                <div className="flex items-start">
                  <div
                    className={`w-4 h-4 rounded-full border flex items-center justify-center mr-3 mt-1 ${
                      isSelected ? 'border-[#621C43]' : 'border-gray-400'
                    }`}
                  >
                    <div
                      className={`w-3 h-3 rounded-full ${
                        isSelected ? 'bg-[#621C43]' : 'bg-transparent'
                      }`}
                    ></div>
                  </div>

                  <div>
                    <div
                      className={`font-medium text-[15px] ${
                        isSelected ? 'text-[#621C43]' : 'text-[#4A082D]'
                      }`}
                    >
                      {option.label}
                    </div>
                    <div className="text-xs text-[#4A082D] font-light">{option.desc}</div>
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      <div className="px-6">
        <label className="block text-sm font-medium text-[#4A082D] mb-1">
          Title / Subject <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={messageData.title}
          onChange={handleTitleChange}
          placeholder="Enter"
          className={`w-full mb-1 p-3 rounded-lg border bg-[#4A082D]/20 text-[#4A082D] 
            ${errors.title ? 'border-red-500' : 'border-gray-300'}
          `}
        />
        {errors.title && <p className="text-red-500 text-sm mb-2">{errors.title}</p>}

        <div className="flex items-center gap-2 mb-1">
          <label className="text-sm font-medium text-[#4A082D]">
            Message <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <button
              type="button"
              className="w-4 h-4 rounded-full bg-[#621C43] text-white text-xs flex items-center justify-center font-bold"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              i
            </button>
            {showTooltip && (
              <div className="absolute left-6 top-1/2 transform -translate-y-1/2 px-3 py-2 bg-gray-800 text-white text-xs rounded whitespace-nowrap z-10">
                Use {'{{'} variableName {'}}'} to add dynamic fields (for bulk upload only)
                <div className="absolute right-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-gray-800"></div>
              </div>
            )}
          </div>
        </div>
        <textarea
          value={messageData.message}
          onChange={handleMessageChange}
          placeholder="Enter message with variables like: Hello {{name}}, how are you?"
          rows={6}
          className={`w-full p-5 rounded-lg border bg-[#4A082D]/20 text-[#4A082D]
            ${errors.message ? 'border-red-500' : 'border-gray-300'}
          `}
        ></textarea>
        <div className="flex justify-between items-center ">
          {errors.message && <p className="text-red-500 text-sm">{errors.message}</p>}
          <div className="ml-auto">
            <span
              className={`text-sm ${
                messageData.message.length > 500 ? 'text-red-500' : 'text-gray-500'
              }`}
            >
              {/* {messageData.message.length}/500 */}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
